import React, { useEffect, useMemo, useRef, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import LastPageIcon from '@mui/icons-material/LastPage';
import LockIcon from '@mui/icons-material/Lock';
import { useGridMode } from 'contexts/GridModeContext';
import { useIsActiveTable } from 'contexts/TableActiveContext';
import { useIsTableOnScreen } from 'contexts/TableOnScreenContext';
import { useHandHistory } from 'hooks/useHandHistory';

import { BoardCards } from './Cards';
import { getPotTotal } from './handSummary';
import { formatGameVariant, formatHandDate, formatMetadata, formatPot } from './format';
import { HandHistoryV1Snapshot } from './HandHistoryV1Snapshot';
import { PlayByPlayList } from './PlayByPlayList';

import styles from './HandHistoryV1.module.scss';

const SWIPE_THRESHOLD = 50;

const HistorySkeleton = () => (
  <div
    className={styles.loadingSkeleton}
    role='status'
    aria-label='Loading hand history'
    aria-busy='true'>
    <span />
    <span />
    <div />
    <span />
    <span />
  </div>
);

/** Original two-tab layout, available only in the playground. */
export const HandHistoryV1Panel = () => {
  const {
    data,
    tableScope,
    error,
    isLoading,
    isPreparing,
    isOpen,
    setIsOpen,
    selectedIndex,
    setSelectedIndex
  } = useHandHistory();
  const isGridMode = useGridMode();
  const isActiveTable = useIsActiveTable();
  const isOnScreen = useIsTableOnScreen();
  const isVisible = isOpen && isOnScreen && (isGridMode || isActiveTable);
  const tableVisibleRef = useRef(false);
  tableVisibleRef.current = isOnScreen && (isGridMode || isActiveTable);
  const [isTransitioning, setIsTransitioning] = useState(Boolean(tableScope && isVisible));
  const wasVisible = useRef(isVisible);
  // Reset before paint: parked panels must never flash their previous content.
  if (wasVisible.current !== isVisible) {
    wasVisible.current = isVisible;
    setIsTransitioning(isVisible);
  }
  useEffect(() => {
    if (!isTransitioning) return;
    const timer = window.setTimeout(() => setIsTransitioning(false), 180);
    return () => window.clearTimeout(timer);
  }, [isTransitioning]);
  const [view, setView] = useState<'active' | 'completed'>('active');
  const panelRef = useRef<HTMLElement>(null);
  const touchStartX = useRef<number | null>(null);
  const handCount = data.completedHands.length;
  const selectedHand = data.completedHands[selectedIndex];
  const showSkeleton = isPreparing || isTransitioning;
  const selectedPotTotal = selectedHand ? getPotTotal(selectedHand) : 0;

  const canGoPrevious = selectedIndex > 0;
  const canGoNext = selectedIndex < handCount - 1;

  const goTo = (nextIndex: number) => {
    setView('completed');
    setSelectedIndex(Math.max(0, Math.min(handCount - 1, nextIndex)));
  };

  useEffect(() => {
    if (!isVisible) return;
    const previousFocus = document.activeElement as HTMLElement | null;
    panelRef.current?.querySelector<HTMLButtonElement>('button')?.focus();
    return () => {
      if (tableVisibleRef.current && previousFocus?.isConnected) previousFocus.focus();
    };
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return;
    const onKeyDown = (event: KeyboardEvent) => {
      const panel = panelRef.current;
      if (!(event.target instanceof Node) || !panel?.contains(event.target)) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        setIsOpen(false);
        return;
      }
      const target = event.target as HTMLElement;
      if (target.closest('input, textarea, select, [role="tab"], [contenteditable="true"]')) return;
      if (event.key === 'ArrowLeft' && canGoPrevious) {
        event.preventDefault();
        goTo(selectedIndex - 1);
      }
      if (event.key === 'ArrowRight' && canGoNext) {
        event.preventDefault();
        goTo(selectedIndex + 1);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [canGoNext, canGoPrevious, isVisible, selectedIndex, setIsOpen]);

  const activeTitle = useMemo(() => {
    if (!data.activeHand) return 'Active hand';
    return `${formatGameVariant(data.activeHand.gameVariant, data.activeHand.bettingStructure)} #${
      data.activeHand.gameNo
    }`;
  }, [data.activeHand]);

  if (!isVisible) return null;

  return (
    <aside
      ref={panelRef}
      aria-label='Hand History'
      className={[
        styles.panel,
        isGridMode ? styles.panelGrid : '',
        isOpen ? styles.panelOpen : styles.panelClosed
      ]
        .filter(Boolean)
        .join(' ')}
      aria-hidden={!isOpen}
      onTouchStart={(event) => {
        if (!isOpen) return;
        touchStartX.current = event.touches[0]?.clientX ?? null;
      }}
      onTouchEnd={(event) => {
        if (!isOpen) return;
        if (touchStartX.current === null) return;
        const delta = event.changedTouches[0].clientX - touchStartX.current;
        touchStartX.current = null;

        if (Math.abs(delta) < SWIPE_THRESHOLD) return;
        if (delta > 0 && canGoPrevious) goTo(selectedIndex - 1);
        if (delta < 0 && canGoNext) goTo(selectedIndex + 1);
      }}>
      <header className={styles.panelHeader}>
        <div>
          <span className={styles.kicker}>V1 · Active / Completed</span>
          <h1>Hand History</h1>
        </div>
        <button
          className={styles.iconButton}
          type='button'
          aria-label='Close'
          onClick={() => setIsOpen(false)}>
          <CloseIcon />
        </button>
      </header>

      {showSkeleton ? (
        <HistorySkeleton />
      ) : data.locked ? (
        <div className={styles.lockedState}>
          <LockIcon />
          <h2>Be dealt in to unlock hand history for this session.</h2>
          <p>History starts from the hand that makes you eligible.</p>
        </div>
      ) : (
        <>
          <div
            className={styles.segmentedControl}
            role='tablist'
            aria-label='Hand history views'
            onKeyDown={(event) => {
              if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
              event.preventDefault();
              const next =
                event.key === 'Home'
                  ? 'active'
                  : event.key === 'End'
                  ? 'completed'
                  : view === 'active'
                  ? 'completed'
                  : 'active';
              setView(next);
              panelRef.current
                ?.querySelector<HTMLButtonElement>(`#history-v1-${next}-tab`)
                ?.focus();
            }}>
            <button
              type='button'
              role='tab'
              id='history-v1-active-tab'
              aria-controls='history-v1-active'
              aria-selected={view === 'active'}
              tabIndex={view === 'active' ? 0 : -1}
              className={view === 'active' ? styles.segmentActive : ''}
              onClick={() => setView('active')}>
              <span>Active</span>
              <small>Current round</small>
            </button>
            <button
              type='button'
              role='tab'
              id='history-v1-completed-tab'
              aria-controls='history-v1-completed'
              aria-selected={view === 'completed'}
              tabIndex={view === 'completed' ? 0 : -1}
              className={view === 'completed' ? styles.segmentActive : ''}
              onClick={() => {
                setSelectedIndex(selectedIndex);
                setView('completed');
              }}>
              <span>Completed</span>
              <small>{handCount} concluded</small>
            </button>
          </div>

          {view === 'active' && (
            <div
              className={styles.activeHand}
              role='tabpanel'
              id='history-v1-active'
              aria-labelledby='history-v1-active-tab'>
              {data.activeHand && (
                <>
                  <div className={styles.handHero}>
                    <div>
                      <span className={styles.kicker}>{activeTitle}</span>
                      <h2>{formatMetadata(data.activeHand.metadata, data.activeHand.gameType)}</h2>
                    </div>
                  </div>
                  <section className={styles.section}>
                    <div className={styles.sectionHeader}>
                      <span>Board</span>
                    </div>
                    <BoardCards cards={data.activeHand.board} />
                  </section>
                </>
              )}
              <section className={styles.playByPlaySection}>
                <div className={styles.playByPlayToggleStatic}>
                  <span>Play-by-play</span>
                  <strong>{data.activeHand?.playByPlay.length ?? 0} events</strong>
                </div>
                <PlayByPlayList
                  showTableCards
                  events={data.activeHand?.playByPlay ?? []}
                  gameType={data.activeHand?.gameType ?? 'cash'}
                />
              </section>
            </div>
          )}

          {view === 'completed' && selectedHand && (
            <div
              className={styles.completedHand}
              role='tabpanel'
              id='history-v1-completed'
              aria-labelledby='history-v1-completed-tab'>
              <nav className={styles.handNav} aria-label='Completed hand navigation'>
                <div className={styles.handNavControls}>
                  <button
                    className={styles.iconButton}
                    type='button'
                    aria-label='First hand'
                    disabled={!canGoPrevious}
                    onClick={() => goTo(0)}>
                    <FirstPageIcon />
                  </button>
                  <button
                    className={styles.iconButton}
                    type='button'
                    aria-label='Previous hand'
                    disabled={!canGoPrevious}
                    onClick={() => goTo(selectedIndex - 1)}>
                    <KeyboardArrowLeftIcon />
                  </button>
                  <span className={styles.handCounter}>
                    {selectedIndex + 1} / {handCount}
                  </span>
                  <button
                    className={styles.iconButton}
                    type='button'
                    aria-label='Next hand'
                    disabled={!canGoNext}
                    onClick={() => goTo(selectedIndex + 1)}>
                    <KeyboardArrowRightIcon />
                  </button>
                  <button
                    className={styles.iconButton}
                    type='button'
                    aria-label='Last hand'
                    disabled={!canGoNext}
                    onClick={() => goTo(handCount - 1)}>
                    <LastPageIcon />
                  </button>
                </div>
                <span className={styles.handNavPot}>
                  Pot <strong>{formatPot(selectedPotTotal, selectedHand.gameType)}</strong>
                </span>
              </nav>
              <section className={styles.section}>
                <div className={styles.sectionHeader}>
                  <span>Game No</span>
                  <strong>
                    {selectedHand.tableId || selectedHand.gameId}-{selectedHand.gameNo}
                  </strong>
                </div>
              </section>
              <section className={styles.section}>
                <div className={styles.sectionHeader}>
                  <span>Date</span>
                  <strong>{formatHandDate(selectedHand.completedAt)}</strong>
                </div>
              </section>
              <HandHistoryV1Snapshot key={selectedHand.handId} hand={selectedHand} />
            </div>
          )}

          {view === 'completed' && !selectedHand && isLoading && <HistorySkeleton />}
          {view === 'completed' && !selectedHand && !isLoading && (
            <div className={styles.emptyState}>
              <h2>No concluded hands yet.</h2>
              <p>{error || 'Completed hands will appear here after the pot is awarded.'}</p>
            </div>
          )}
        </>
      )}
    </aside>
  );
};
